import {
  createApprovalGrant,
  evaluateToolCallPolicy,
  type FabricPrincipal,
  type JsonObject,
  type SecretProvider,
  type ToolDef,
} from "@fabric-harness/sdk";
import {
  type DatabricksAuthoringLifecycleEvidence,
  type DatabricksCertificationCheck,
  runDatabricksAuthoringLifecycle,
} from "./certification.js";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import {
  databricksAiSearchAdminTools,
  DatabricksAiSearchAdmin,
  type DatabricksSearchIndexSpec,
  type DatabricksAiSearchAdminClient,
} from "./ai-search-admin.js";
import {
  type DatabricksComputePolicy,
  DatabricksJobsAuthoring,
  databricksJobsAuthoringTools,
  type DatabricksJobSpec,
  type DatabricksJobsAuthoringClient,
} from "./jobs-authoring.js";
import {
  DatabricksLakeflowAuthoring,
  databricksLakeflowAuthoringTools,
  type DatabricksPipelineSpec,
  type DatabricksPipelinesAuthoringClient,
} from "./lakeflow-authoring.js";
import type { DatabricksStatementClient } from "./sql-exec.js";
import {
  DatabricksServingAdmin,
  databricksServingAdminTools,
  type DatabricksServingEndpointSpec,
  type DatabricksServingAdminClient,
} from "./serving-admin.js";
import { databricksSecretsAuthoringTools } from "./secrets-authoring.js";
import type { DatabricksSecretsClient } from "./secrets-authoring.js";
import { databricksUnityCatalogAdminTools } from "./unity-catalog-admin.js";
import type { DatabricksUnityCatalogAdminClients } from "./unity-catalog-admin.js";
import { databricksWorkspaceAuthoringTools } from "./workspace-authoring.js";
import { databricksGovernancePolicy, withGovernance } from "./governance.js";
import {
  DatabricksGenieAdmin,
  databricksGenieAuthoringTools,
  type DatabricksGenieAgentSpecV2,
  type DatabricksGenieSqlPolicy,
} from "./genie-authoring.js";
import { databricksGenie } from "./genie.js";
import type { DatabricksGenieSdkClient } from "./genie.js";
import type { GenieClient } from "@databricks/sdk-genie/v1";
import type { AccessManagementClient } from "@databricks/sdk-accessmanagement/v1";
import type { ScimClient } from "@databricks/sdk-scim/v1";
import type { PipelinesClient } from "@databricks/sdk-pipelines/v2";
import type { SecretsClient } from "@databricks/sdk-secrets/v1";
import { MemoryDatabricksManagedResourceStore } from "./managed-resource.js";

export interface DatabricksAuthoringCertificationFixtures {
  enabled: boolean;
  host: string;
  client: DatabricksRawProtocolClient;
  jobsClient?: DatabricksJobsAuthoringClient;
  pipelinesClient?: DatabricksPipelinesAuthoringClient & Pick<PipelinesClient, "get">;
  statementsClient?: DatabricksStatementClient;
  vectorSearchClient?: DatabricksAiSearchAdminClient;
  modelServingClient?: DatabricksServingAdminClient;
  unityCatalogClients?: DatabricksUnityCatalogAdminClients;
  secretsClient?: DatabricksSecretsClient & Pick<SecretsClient, "listScopes" | "listSecrets">;
  scimClient?: Pick<ScimClient, "me">;
  genieClient?: GenieClient;
  accessManagementClient?: AccessManagementClient;
  executingPrincipal: FabricPrincipal;
  authMode?: string;
  steward: { id: string; displayName?: string };
  jobs?: { spec: DatabricksJobSpec; mutateName?: string; computePolicy?: DatabricksComputePolicy };
  classicJobs?: {
    spec: DatabricksJobSpec;
    mutateName?: string;
    computePolicy?: DatabricksComputePolicy;
  };
  lakeflow?: { spec: DatabricksPipelineSpec; mutateName?: string };
  aiSearch?: { endpointName: string; index: DatabricksSearchIndexSpec };
  deltaAiSearch?: { endpointName: string; index: DatabricksSearchIndexSpec };
  serving?: { spec: DatabricksServingEndpointSpec };
  provisionedServing?: { spec: DatabricksServingEndpointSpec };
  unityCatalog?: { catalog: string; schemaName: string; oboPrincipal: string };
  workspace?: { rootPath: string };
  secrets?: { scope: string; key: string; secretName: string; provider: SecretProvider };
  genie?: {
    spec: DatabricksGenieAgentSpecV2;
    question: string;
    sqlPolicy?: DatabricksGenieSqlPolicy;
  };
}

class GovernedCertificationRunner {
  readonly lineage: Array<{ tool: string; ok: boolean; approvers?: unknown[] }> = [];
  private calls = 0;

  constructor(private readonly fixtures: DatabricksAuthoringCertificationFixtures) {}

  async execute<T>(tool: ToolDef, input: JsonObject): Promise<T> {
    const service = String(tool.metadata?.service ?? "");
    const policy = databricksGovernancePolicy({
      tools: [tool],
      host: this.fixtures.host,
      stewardAudience: "databricks-certification-stewards",
      approvalServices: [service],
    });
    const decision = evaluateToolCallPolicy({ tool: tool.name, input }, policy);
    if (
      !decision.approvalRequired ||
      decision.approvalAudience !== "databricks-certification-stewards"
    ) {
      throw new Error(
        `Databricks certification tool ${tool.name} did not route to steward approval.`,
      );
    }
    const toolCallId = `databricks-certification:${tool.name}:${++this.calls}`;
    const grant = createApprovalGrant({
      approvalId: `approval:${toolCallId}`,
      toolCallId,
      toolInput: input,
      principal: this.fixtures.executingPrincipal,
      response: {
        decision: "approved",
        approvers: [
          {
            id: this.fixtures.steward.id,
            type: "user",
            ...(this.fixtures.steward.displayName
              ? { displayName: this.fixtures.steward.displayName }
              : {}),
          },
        ],
      },
      createdAt: new Date().toISOString(),
    });
    const governed = withGovernance(tool, {
      executingPrincipal: this.fixtures.executingPrincipal,
      requireApprovalGrantFor: [service],
      onLineage: (record) => this.lineage.push(record),
    });
    return (await governed.execute?.(input, { toolCallId, approval: grant } as never)) as T;
  }

  evidence(lifecycle?: DatabricksAuthoringLifecycleEvidence): Record<string, unknown> {
    const approved = this.lineage.filter((row) => row.ok && row.approvers?.length).length;
    if (approved !== this.calls) {
      throw new Error(
        `Databricks certification lineage captured ${approved}/${this.calls} approved calls.`,
      );
    }
    return {
      ...(lifecycle ?? {}),
      approvedToolCalls: this.calls,
      lineageRecords: this.lineage.length,
      executingPrincipal: this.fixtures.executingPrincipal.id,
      steward: this.fixtures.steward.id,
    };
  }
}

export function createDatabricksAuthoringCertificationChecks(
  fixtures: DatabricksAuthoringCertificationFixtures,
): DatabricksCertificationCheck[] {
  return [
    check("approval-provenance", fixtures.enabled, () => approvalProvenance(fixtures)),
    check("jobs-authoring", fixtures.enabled && Boolean(fixtures.jobs), () =>
      jobsLifecycle(fixtures, requiredFixture(fixtures.jobs, "jobs"), "job"),
    ),
    check("jobs-classic-authoring", fixtures.enabled && Boolean(fixtures.classicJobs), () =>
      jobsLifecycle(fixtures, requiredFixture(fixtures.classicJobs, "classicJobs"), "classic-job"),
    ),
    check("lakeflow-authoring", fixtures.enabled && Boolean(fixtures.lakeflow), () =>
      lakeflowLifecycle(fixtures),
    ),
    check("ai-search-admin", fixtures.enabled && Boolean(fixtures.aiSearch), () =>
      aiSearchLifecycle(
        fixtures,
        requiredFixture(fixtures.aiSearch, "aiSearch"),
        "ai-search-endpoint",
      ),
    ),
    check("ai-search-delta-sync-admin", fixtures.enabled && Boolean(fixtures.deltaAiSearch), () =>
      aiSearchLifecycle(
        fixtures,
        requiredFixture(fixtures.deltaAiSearch, "deltaAiSearch"),
        "ai-search-delta-sync-endpoint",
      ),
    ),
    check("serving-admin", fixtures.enabled && Boolean(fixtures.serving), () =>
      servingLifecycle(fixtures, requiredFixture(fixtures.serving, "serving"), "serving-endpoint"),
    ),
    check(
      "serving-provisioned-throughput-admin",
      fixtures.enabled && Boolean(fixtures.provisionedServing),
      () =>
        servingLifecycle(
          fixtures,
          requiredFixture(fixtures.provisionedServing, "provisionedServing"),
          "serving-provisioned-throughput-endpoint",
        ),
    ),
    check(
      "uc-admin-obo-execution",
      fixtures.enabled && fixtures.authMode === "on-behalf-of" && Boolean(fixtures.unityCatalog),
      () => unityCatalogLifecycle(fixtures),
    ),
    check("workspace-write", fixtures.enabled && Boolean(fixtures.workspace), () =>
      workspaceLifecycle(fixtures),
    ),
    check("secrets-write", fixtures.enabled && Boolean(fixtures.secrets), () =>
      secretsLifecycle(fixtures),
    ),
    check("genie-authoring", fixtures.enabled && Boolean(fixtures.genie), () =>
      genieLifecycle(fixtures),
    ),
  ];
}

function check(
  id: string,
  configured: boolean,
  run: () => Promise<Record<string, unknown>>,
): DatabricksCertificationCheck {
  return {
    id,
    description: `Protected authoring lifecycle evidence for ${id}: create, verify, mutate, delete, approval consumption, lineage, and cleanup.`,
    configured,
    run: async () => ({ ...(await run()), evidenceChecks: authoringEvidenceChecks(id) }),
  };
}

function authoringEvidenceChecks(id: string): string[] {
  const prefix =
    id === "jobs-authoring" || id === "jobs-classic-authoring"
      ? "jobs"
      : id === "lakeflow-authoring"
        ? "lakeflow"
        : id === "ai-search-admin" || id === "ai-search-delta-sync-admin"
          ? "ai-search-admin"
          : id === "serving-admin" || id === "serving-provisioned-throughput-admin"
            ? "serving"
            : id === "uc-admin-obo-execution"
              ? "uc-admin"
              : id === "workspace-write"
                ? "workspace"
                : id === "secrets-write"
                  ? "secrets"
                  : id === "genie-authoring"
                    ? "genie-authoring"
                    : undefined;
  if (!prefix) return ["approval.request", "approval.consume", "lineage.write"];
  return [
    `${prefix}.create`,
    `${prefix}.verify`,
    `${prefix}.mutate`,
    ...(prefix === "genie-authoring" ? ["genie-authoring.query"] : []),
    `${prefix}.delete`,
    ...(prefix === "uc-admin" ? ["uc-admin.obo-execution"] : []),
    "approval.consume",
    "lineage.write",
  ];
}

async function approvalProvenance(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const runner = new GovernedCertificationRunner(fixtures);
  const tool: ToolDef<JsonObject, JsonObject> = {
    name: "databricks_certification_approval_probe",
    inputSchema: {
      type: "object",
      properties: { resource: { type: "string" } },
      required: ["resource"],
    },
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "certification",
      governance: { resources: [{ kind: "probe", path: "/resource" }] },
    },
    execute: (input) => input,
  };
  const result = await runner.execute<JsonObject>(tool, { resource: "approval-provenance" });
  if (result.resource !== "approval-provenance")
    throw new Error("Approval probe returned the wrong resource.");
  return runner.evidence();
}

async function jobsLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
  config: NonNullable<DatabricksAuthoringCertificationFixtures["jobs"]>,
  kind: "job" | "classic-job",
): Promise<Record<string, unknown>> {
  const jobsClient = requiredFixture(fixtures.jobsClient, "jobs native SDK client");
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksJobsAuthoringTools(jobsClient, config.computePolicy);
  const api = new DatabricksJobsAuthoring(jobsClient, config.computePolicy);
  let jobId = 0;
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind,
    create: async () => {
      const created = await runner.execute<{ jobId: number }>(
        findTool(tools, "databricks_create_job"),
        config.spec as unknown as JsonObject,
      );
      jobId = created.jobId;
      return created;
    },
    identify: (created) => String(created.jobId),
    verifyCreated: async (created) => {
      const row = await api.getJob(created.jobId);
      if (!row.settings) throw new Error("Created Databricks job was not readable.");
    },
    mutate: async (created) => {
      const name = config.mutateName ?? `${config.spec.name}-verified`;
      await runner.execute(findTool(tools, "databricks_update_job"), {
        jobId: created.jobId,
        set: { name },
      });
      return name;
    },
    verifyMutated: async (created, name) => {
      const row = await api.getJob(created.jobId);
      if ((row.settings as JsonObject | undefined)?.name !== name)
        throw new Error("Databricks job mutation was not observable.");
    },
    delete: (created) =>
      runner.execute(findTool(tools, "databricks_delete_job"), { jobId: created.jobId }),
    verifyDeleted: () => expectNotFound(() => api.getJob(jobId)),
  });
  return runner.evidence(lifecycle);
}

async function lakeflowLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const config = requiredFixture(fixtures.lakeflow, "lakeflow");
  const pipelinesClient = requiredFixture(fixtures.pipelinesClient, "pipelines native SDK client");
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksLakeflowAuthoringTools(pipelinesClient);
  const api = new DatabricksLakeflowAuthoring(pipelinesClient, fixtures.statementsClient);
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind: "pipeline",
    create: () =>
      runner.execute<{ pipelineId: string }>(
        findTool(tools, "databricks_create_pipeline"),
        config.spec as unknown as JsonObject,
      ),
    identify: (created) => created.pipelineId,
    verifyCreated: (created) => pipelinesClient.get({ pipelineId: created.pipelineId }),
    mutate: async (created) => {
      const name = config.mutateName ?? `${config.spec.name}-verified`;
      await runner.execute(findTool(tools, "databricks_update_pipeline"), {
        pipelineId: created.pipelineId,
        ...config.spec,
        name,
      } as unknown as JsonObject);
      return name;
    },
    verifyMutated: async (created, name) => {
      const row = await pipelinesClient.get({ pipelineId: created.pipelineId });
      if (row.spec?.name !== name) throw new Error("Lakeflow mutation was not observable.");
    },
    delete: (created) =>
      runner.execute(findTool(tools, "databricks_delete_pipeline"), {
        pipelineId: created.pipelineId,
      }),
    verifyDeleted: (created) =>
      expectNotFound(() => pipelinesClient.get({ pipelineId: created.pipelineId })),
  });
  void api;
  return runner.evidence(lifecycle);
}

async function aiSearchLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
  config: NonNullable<DatabricksAuthoringCertificationFixtures["aiSearch"]>,
  kind: "ai-search-endpoint" | "ai-search-delta-sync-endpoint",
): Promise<Record<string, unknown>> {
  const vectorSearchClient = requiredFixture(
    fixtures.vectorSearchClient,
    "vector search native SDK client",
  );
  const runner = new GovernedCertificationRunner(fixtures);
  // Certification drives the bounded path end-to-end: only the fixture's own endpoint and index
  // names are reachable through the tools.
  const tools = databricksAiSearchAdminTools(vectorSearchClient, {
    allowedOperations: ["createEndpoint", "deleteEndpoint", "createIndex", "deleteIndex"],
    allowedEndpoints: [config.endpointName, config.index.endpointName],
    allowedIndexes: [config.index.name],
    // The direct-vector fixture omits the embedding arm entirely, so certification proves the
    // pruned path: delta-sync creation is absent from the schema, not merely unbounded.
    ...(config.index.kind === "deltaSync"
      ? {
          allowedEmbeddingModelEndpoints: [config.index.embedding.modelEndpoint],
          allowedSourceTables: [config.index.sourceTable],
        }
      : {}),
  });
  const api = new DatabricksAiSearchAdmin(vectorSearchClient);
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind,
    create: async () => {
      await runner.execute(findTool(tools, "databricks_create_search_endpoint"), {
        name: config.endpointName,
        endpointType: "STANDARD",
      });
      await api.waitForEndpoint(config.endpointName);
      return { id: config.endpointName };
    },
    identify: (created) => created.id,
    verifyCreated: () => api.getEndpoint(config.endpointName),
    mutate: async () => {
      await runner.execute(
        findTool(tools, "databricks_create_search_index"),
        config.index as unknown as JsonObject,
      );
      return config.index.name;
    },
    verifyMutated: (_created, indexName) => api.describeIndex(indexName),
    delete: async () => {
      await runner.execute(findTool(tools, "databricks_delete_search_index"), {
        name: config.index.name,
      });
      await runner.execute(findTool(tools, "databricks_delete_search_endpoint"), {
        name: config.endpointName,
      });
    },
    verifyDeleted: () => expectNotFound(() => api.getEndpoint(config.endpointName)),
  });
  return runner.evidence(lifecycle);
}

async function servingLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
  config: NonNullable<DatabricksAuthoringCertificationFixtures["serving"]>,
  kind: "serving-endpoint" | "serving-provisioned-throughput-endpoint",
): Promise<Record<string, unknown>> {
  const modelServingClient = requiredFixture(
    fixtures.modelServingClient,
    "model serving native SDK client",
  );
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksServingAdminTools(modelServingClient);
  const api = new DatabricksServingAdmin(modelServingClient);
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind,
    create: async () => {
      await runner.execute(
        findTool(tools, "databricks_create_serving_endpoint"),
        config.spec as unknown as JsonObject,
      );
      await api.waitForEndpoint(config.spec.name);
      return { id: config.spec.name };
    },
    identify: (created) => created.id,
    verifyCreated: () => api.getEndpoint(config.spec.name),
    mutate: async () => {
      await runner.execute(findTool(tools, "databricks_update_serving_endpoint"), {
        name: config.spec.name,
        servedEntities: config.spec.servedEntities,
      } as unknown as JsonObject);
      return api.waitForEndpoint(config.spec.name);
    },
    verifyMutated: () => api.getEndpoint(config.spec.name),
    delete: () =>
      runner.execute(findTool(tools, "databricks_delete_serving_endpoint"), {
        name: config.spec.name,
      }),
    verifyDeleted: () => expectNotFound(() => api.getEndpoint(config.spec.name)),
  });
  return runner.evidence(lifecycle);
}

async function unityCatalogLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const config = requiredFixture(fixtures.unityCatalog, "unityCatalog");
  if (fixtures.executingPrincipal.ucPrincipal !== config.oboPrincipal) {
    throw new Error("UC certification requires the executing OBO principal to match oboPrincipal.");
  }
  const scimClient = requiredFixture(fixtures.scimClient, "SCIM native SDK client");
  const currentUser = await scimClient.me({});
  if (
    currentUser.userName !== config.oboPrincipal &&
    currentUser.id !== fixtures.executingPrincipal.id
  ) {
    throw new Error("UC certification OBO token resolved to a different Databricks principal.");
  }
  const unityCatalogClients = requiredFixture(
    fixtures.unityCatalogClients,
    "Unity Catalog native SDK clients",
  );
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksUnityCatalogAdminTools(unityCatalogClients, { destructive: true });
  const fullName = `${config.catalog}.${config.schemaName}`;
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind: "schema",
    create: async () => {
      await runner.execute(findTool(tools, "databricks_create_schema"), {
        catalogName: config.catalog,
        name: config.schemaName,
        comment: "Fabric Harness protected lifecycle certification",
      });
      return { id: fullName };
    },
    identify: (created) => created.id,
    verifyCreated: () => unityCatalogClients.schemas.getSchema({ fullNameArg: fullName }),
    mutate: async () => {
      await runner.execute(findTool(tools, "databricks_grant"), {
        securableType: "schema",
        fullName,
        principal: config.oboPrincipal,
        privileges: ["USE_SCHEMA"],
      });
      return "USE_SCHEMA";
    },
    verifyMutated: async () => {
      const permissions = await unityCatalogClients.grants.getPermissions({
        securableType: "schema",
        securableFullName: fullName,
      });
      if (!JSON.stringify(permissions).includes(config.oboPrincipal))
        throw new Error("UC grant was not observable for the OBO principal.");
    },
    delete: () =>
      runner.execute(findTool(tools, "databricks_delete_uc_object"), { kind: "schema", fullName }),
    verifyDeleted: () =>
      expectNotFound(() => unityCatalogClients.schemas.getSchema({ fullNameArg: fullName })),
  });
  return runner.evidence(lifecycle);
}

async function workspaceLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const config = requiredFixture(fixtures.workspace, "workspace");
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksWorkspaceAuthoringTools(fixtures.client);
  const root = config.rootPath.replace(/\/$/, "");
  const notebook = `${root}/probe.py`;
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind: "workspace-directory",
    create: async () => {
      await runner.execute(findTool(tools, "databricks_workspace_mkdirs"), { path: root });
      return { id: root };
    },
    identify: (created) => created.id,
    verifyCreated: () => fixtures.client.get("/api/2.0/workspace/get-status", { path: root }),
    mutate: () =>
      runner.execute(findTool(tools, "databricks_import_notebook"), {
        path: notebook,
        content: "# Fabric Harness certification\n",
        language: "PYTHON",
        format: "SOURCE",
      }),
    verifyMutated: () => fixtures.client.get("/api/2.0/workspace/get-status", { path: notebook }),
    delete: () =>
      runner.execute(findTool(tools, "databricks_delete_workspace_object"), {
        path: root,
        recursive: true,
      }),
    verifyDeleted: () =>
      expectNotFound(() => fixtures.client.get("/api/2.0/workspace/get-status", { path: root })),
  });
  return runner.evidence(lifecycle);
}

async function secretsLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const config = requiredFixture(fixtures.secrets, "secrets");
  const secretsClient = requiredFixture(fixtures.secretsClient, "secrets native SDK client");
  const runner = new GovernedCertificationRunner(fixtures);
  const tools = databricksSecretsAuthoringTools(secretsClient, config.provider);
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind: "secret-scope",
    create: async () => {
      await runner.execute(findTool(tools, "databricks_create_secret_scope"), {
        scope: config.scope,
      });
      return { id: config.scope };
    },
    identify: (created) => created.id,
    verifyCreated: () => verifySecretScope(secretsClient, config.scope),
    mutate: () =>
      runner.execute(findTool(tools, "databricks_put_secret"), {
        scope: config.scope,
        key: config.key,
        secretRef: { kind: "secret", name: config.secretName },
      }),
    verifyMutated: async () => {
      const keys = await secretsClient.listSecrets({ scope: config.scope });
      if (!keys.secrets?.some((row: { key?: string | undefined }) => row.key === config.key))
        throw new Error("Written secret key was not listed.");
    },
    delete: () =>
      runner.execute(findTool(tools, "databricks_delete_secret_scope"), { scope: config.scope }),
    verifyDeleted: () => verifySecretScopeMissing(secretsClient, config.scope),
  });
  return runner.evidence(lifecycle);
}

async function genieLifecycle(
  fixtures: DatabricksAuthoringCertificationFixtures,
): Promise<Record<string, unknown>> {
  const config = requiredFixture(fixtures.genie, "genie");
  const runner = new GovernedCertificationRunner(fixtures);
  const store = new MemoryDatabricksManagedResourceStore();
  const genieClient = requiredFixture(fixtures.genieClient, "Genie native SDK client");
  const adminClients = {
    genie: genieClient,
    permissions: requiredFixture(
      fixtures.accessManagementClient,
      "Access Management native SDK client",
    ),
  };
  const tools = databricksGenieAuthoringTools(adminClients, {
    managedResourceStore: store,
    principal: fixtures.executingPrincipal.ucPrincipal ?? fixtures.executingPrincipal.id,
    destructive: true,
    ...(config.sqlPolicy ? { sqlPolicy: config.sqlPolicy } : {}),
  });
  const api = new DatabricksGenieAdmin(adminClients, { managedResourceStore: store });
  let currentFingerprint = "";
  const lifecycle = await runDatabricksAuthoringLifecycle({
    kind: "genie-agent",
    create: async () => {
      const created = await runner.execute<{ agentId: string; fingerprint: string }>(
        findTool(tools, "databricks_create_genie_agent"),
        { spec: config.spec as unknown as JsonObject },
      );
      currentFingerprint = created.fingerprint;
      return created;
    },
    identify: (created) => created.agentId,
    verifyCreated: async (created) => {
      const row = await api.get(created.agentId, { includeConfiguration: true });
      if (!row.serializedSpace)
        throw new Error("Created Genie Agent configuration was not readable.");
      const permissions = await api.permissions(created.agentId);
      // Access Management canonicalizes Genie space UUIDs to an internal `/genie/<id>` object ID.
      // Prove that the SDK decoded the canonical object and its owner ACL instead of comparing two
      // identifiers from different Databricks services.
      if (
        !permissions.objectId ||
        permissions.objectType !== "genie" ||
        permissions.accessControlList.length === 0
      ) {
        throw new Error("Created Genie Agent permissions were not readable through Access Management.");
      }
      const managed = await store.get("genie-agent", created.agentId);
      if (!managed) throw new Error("Created Genie Agent had no managed-resource provenance.");
    },
    mutate: async (created) => {
      const nextSpec: DatabricksGenieAgentSpecV2 = {
        ...config.spec,
        sampleQuestions: [
          ...(config.spec.sampleQuestions ?? []),
          { question: "Fabric Harness certification mutation" },
        ],
      };
      const updated = await runner.execute<{ fingerprint: string }>(
        findTool(tools, "databricks_update_genie_agent"),
        {
          agentId: created.agentId,
          expectedFingerprint: currentFingerprint,
          set: { spec: nextSpec as unknown as JsonObject },
        },
      );
      currentFingerprint = updated.fingerprint;
      return nextSpec;
    },
    verifyMutated: async (created) => {
      const row = await api.get(created.agentId, { includeConfiguration: true });
      if (!row.serializedSpace?.includes("Fabric Harness certification mutation"))
        throw new Error("Genie Agent mutation was not observable.");
      const answer = await databricksGenie(
        requiredFixture(fixtures.genieClient, "Genie native SDK client"),
        { agentId: created.agentId, rawProtocolClient: fixtures.client },
      ).ask({
        question: config.question,
      });
      if (!answer.conversationId || !answer.messageId)
        throw new Error("Genie Agent query did not return conversation provenance.");
    },
    delete: (created) =>
      runner.execute(findTool(tools, "databricks_delete_genie_agent"), {
        agentId: created.agentId,
        expectedFingerprint: currentFingerprint,
      }),
    verifyDeleted: async (created) => {
      let pageToken: string | undefined;
      do {
        const page = await api.list({ pageSize: 100, ...(pageToken ? { pageToken } : {}) });
        if (page.agents.some((agent) => agent.agentId === created.agentId))
          throw new Error("Trashed Genie Agent remained in the active inventory.");
        pageToken = page.nextPageToken;
      } while (pageToken);
    },
  });
  return { ...runner.evidence(lifecycle), queryVerified: true, permissionsVerified: true };
}

function findTool(tools: ToolDef[], name: string): ToolDef {
  const tool = tools.find((candidate) => candidate.name === name);
  if (!tool) throw new Error(`Databricks certification tool ${name} is unavailable.`);
  return tool;
}

function requiredFixture<T>(value: T | undefined, name: string): T {
  if (!value) throw new Error(`Databricks authoring certification fixture ${name} is missing.`);
  return value;
}

async function expectNotFound(operation: () => Promise<unknown>): Promise<void> {
  try {
    await operation();
  } catch (error) {
    if (
      /\b404\b|not[ _-]?found|does not exist|RESOURCE_DOES_NOT_EXIST|has been trashed/i.test(
        error instanceof Error ? error.message : String(error),
      )
    )
      return;
    throw error;
  }
  throw new Error("Deleted Databricks resource is still readable.");
}

async function verifySecretScope(
  client: DatabricksSecretsClient & Pick<SecretsClient, "listScopes">,
  scope: string,
): Promise<void> {
  const scopes = await client.listScopes({});
  if (!scopes.scopes?.some((row: { name?: string | undefined }) => row.name === scope))
    throw new Error("Created secret scope was not listed.");
}

async function verifySecretScopeMissing(
  client: DatabricksSecretsClient & Pick<SecretsClient, "listScopes">,
  scope: string,
): Promise<void> {
  const scopes = await client.listScopes({});
  if (scopes.scopes?.some((row: { name?: string | undefined }) => row.name === scope))
    throw new Error("Deleted secret scope is still listed.");
}
